import React, {useRef, useCallback, useEffect, useImperativeHandle, forwardRef} from 'react'
import {Box, StackProps, useBreakpointValue, useMultiStyleConfig} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination, FreeMode, Mousewheel, Keyboard} from 'swiper/modules'
import type SwiperType from 'swiper'
import {ProductImage} from '_uiComponents'
import {useHeaderSizeWithoutBanner, useViewport} from '_customHooks'
import type {MediaItem, VideoSource} from '_utils'
export type {MediaItem} from '_utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

type CarouselStyles = Record<string, StackProps>

/**
 * Imperative handle used by the PDP for desktop scroll-gating. The parent owns the wheel listener
 * (bound to the pinned images+details section element — so scrolling anywhere over that section
 * drives the gallery, while overlays portaled to document.body scroll natively) and calls these to
 * move the vertical Swiper and read its edge state.
 */
export interface ProductMediaCarouselHandle {
    /** Move the gallery by a pixel delta (positive = scroll down/forward), clamped to the edges. */
    driveBy: (deltaY: number) => void
    /** Whether the gallery is fully scrolled to the last image. */
    isAtEnd: () => boolean
    /** Whether the gallery is at the very top (first image). */
    isAtBeginning: () => boolean
    /** Whether the gallery has more content than one screenful (i.e. gating is meaningful). */
    isScrollable: () => boolean
    /** Snap the gallery back to the first image (used when the gate re-arms). */
    resetToStart: () => void
}

export interface ProductMediaCarouselProps {
    mediaItems: MediaItem[]
    productName?: string
    onSlideClick?: (selectedIndex: number) => void
}

/**
 * @name ProductMediaCarousel
 *
 * @description
 * Vertical PDP media carousel. Supports images and video (vertical/horizontal).
 * Lazy loads images via `hasLazy`; video handled internally by the Video component.
 *
 * Uses `aspectRatio: 3/4` as height anchor — Swiper vertical requires a real pixel
 * height; `h="100%"` chains collapse to 0 without a fixed ancestor.
 */
const ProductMediaCarousel = forwardRef<ProductMediaCarouselHandle, ProductMediaCarouselProps>((props, ref) => {
    const {mediaItems, productName = '', onSlideClick} = props
    const headerSize = useHeaderSizeWithoutBanner()
    const {isDesktop} = useViewport()
    const styles = useMultiStyleConfig('ProductDetails', {headerSize}) as Record<string, StackProps>
    // Vertical spacing between gallery slides. Swiper turns `spaceBetween` into an inline
    // `margin-bottom` on each `.swiper-slide`. Design (Figma) wants 5px on mobile and keeps the
    // previous 8px from tablet up. `base` covers mobile (0–767px, i.e. base+sm), `md` covers
    // tablet/desktop; the `8` fallback preserves the pre-existing value during SSR / before the
    // breakpoint resolves, so desktop/tablet behaviour is unchanged.
    const slideSpaceBetween = useBreakpointValue({base: 5, md: 8}) ?? 8
    const swiperRef = useRef<{swiper: SwiperType}>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    // Once the carousel is scrolled to its last slide on mobile touch, we hand touch control
    // back to the page (`false`) so the same gesture keeps scrolling the PDP instead of getting
    // stuck. Control is only handed back to the carousel once the page scrolls back up to the
    // point where the carousel is fully back in view (its original position in the layout).
    const hasReleasedTouchRef = useRef(false)

    const setCarouselTouchEnabled = useCallback((enabled: boolean) => {
        const swiper = swiperRef.current?.swiper
        if (!swiper) return
        swiper.allowTouchMove = enabled
        swiper.el.style.touchAction = enabled ? '' : 'pan-y'
    }, [])

    useEffect(() => {
        const handleScroll = () => {
            if (!hasReleasedTouchRef.current) return
            const el = containerRef.current
            if (!el) return
            if (el.getBoundingClientRect().top >= -1) {
                setCarouselTouchEnabled(true)
                hasReleasedTouchRef.current = false
            }
        }

        window.addEventListener('scroll', handleScroll, {passive: true})
        return () => window.removeEventListener('scroll', handleScroll)
    }, [setCarouselTouchEnabled])

    // ─── Desktop scroll gating (imperative handle) ───────────────────────────────
    // The wheel listener lives in the parent, bound to the pinned images+details section element, so
    // scrolling anywhere over the pinned section drives the gallery (not only over the images) while
    // overlays (portaled to document.body) scroll natively. Here we just expose the primitives to
    // move the vertical Swiper and read its edge state. `setTranslate` is the stable Swiper 11 way to
    // move a free-mode swiper by an arbitrary pixel delta; `maxTranslate()` is the fully-scrolled
    // (last image) translate — negative for vertical — and `minTranslate()` (0) is the top.
    const EDGE_EPS = 1
    useImperativeHandle(
        ref,
        (): ProductMediaCarouselHandle => ({
            driveBy: (deltaY: number) => {
                const swiper = swiperRef.current?.swiper
                if (!swiper) return
                const min = swiper.minTranslate()
                const max = swiper.maxTranslate()
                const next = Math.min(min, Math.max(max, swiper.translate - deltaY))
                swiper.setTranslate(next)
                swiper.updateProgress()
                swiper.updateActiveIndex()
                swiper.pagination?.update?.()
            },
            isAtEnd: () => {
                const swiper = swiperRef.current?.swiper
                if (!swiper) return true
                return swiper.translate <= swiper.maxTranslate() + EDGE_EPS
            },
            isAtBeginning: () => {
                const swiper = swiperRef.current?.swiper
                if (!swiper) return true
                return swiper.translate >= -EDGE_EPS
            },
            isScrollable: () => {
                const swiper = swiperRef.current?.swiper
                if (!swiper) return false
                return swiper.maxTranslate() < swiper.minTranslate() - EDGE_EPS
            },
            resetToStart: () => {
                const swiper = swiperRef.current?.swiper
                if (!swiper) return
                swiper.setTranslate(swiper.minTranslate())
                swiper.updateProgress()
                swiper.updateActiveIndex()
                swiper.pagination?.update?.()
                // Mobile touch handoff: if the user had scrolled to the last image on the previous
                // product, touch was released to the page (`allowTouchMove = false`, pending re-enable
                // by the scroll listener). On a PDP→PDP navigation the gallery is reset here, so also
                // re-arm touch control immediately — otherwise the new gallery could start non-swipeable.
                hasReleasedTouchRef.current = false
                setCarouselTouchEnabled(true)
            },
        }),
        [setCarouselTouchEnabled],
    )

    if (mediaItems.length === 0) return null

    return (
        <>
            <Box sx={styles.mediaCarousel} className="cc-pdp__media-carousel" ref={containerRef}>
                <Box sx={styles.mediaCarouselInner}>
                    <Swiper
                        ref={swiperRef}
                        direction="vertical"
                        slidesPerView="auto"
                        spaceBetween={slideSpaceBetween}
                        modules={[Pagination, FreeMode, Mousewheel, Keyboard]}
                        pagination={{clickable: true}}
                        freeMode={{enabled: true, momentum: true, momentumRatio: 0.5}}
                        // On desktop the wheel is handled manually (see the scroll-gating effect) to pin
                        // the section until every image is seen, so Swiper's own mousewheel is disabled to
                        // avoid double-handling. Mobile keeps the native mousewheel behaviour untouched.
                        mousewheel={isDesktop ? false : {releaseOnEdges: true, forceToAxis: true}}
                        keyboard={{enabled: true}}
                        onSwiper={(swiper) => {
                            swiper.update()
                        }}
                        onReachEnd={() => {
                            hasReleasedTouchRef.current = true
                            setCarouselTouchEnabled(false)
                        }}
                        onTouchMove={(swiper) => {
                            // Handles resuming a new gesture that starts already at the last slide (e.g. right
                            // after the page scrolled back up to the carousel) — `reachEnd` only fires on the
                            // transition into that state, not on a fresh drag that begins there.
                            if (!swiper.allowTouchMove) return
                            const draggingForward = swiper.touches.currentY - swiper.touches.startY < -5
                            if (swiper.isEnd && draggingForward) {
                                hasReleasedTouchRef.current = true
                                setCarouselTouchEnabled(false)
                            }
                        }}
                        style={{width: '100%', height: '100%'}}
                        threshold={10}
                    >
                        {mediaItems.map((item, index) => (
                            <SwiperSlide
                                key={`${item.type}-${index}-${item.isHorizontal ? 'h' : 'v'}`}
                                style={{height: 'auto', cursor: item.type === 'video' ? 'default' : 'pointer'}}
                                onClick={() => {
                                    // Video slides have no zoom equivalent, so they don't open the zoom modal.
                                    if (item.type === 'video') return
                                    onSlideClick?.(index)
                                }}
                            >
                                {item.type === 'video' ? (
                                    <VideoSlideWrapper
                                        src={item.url}
                                        sources={item.sources}
                                        poster={item.poster}
                                        productName={productName}
                                        isHorizontal={item.isHorizontal}
                                        styles={styles}
                                    />
                                ) : (
                                    <ImageSlideWrapper src={item.url} alt={item.alt || productName} isPriority={index === 0} styles={styles} />
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            </Box>
        </>
    )
})

ProductMediaCarousel.displayName = 'ProductMediaCarousel'

// ─── Sub-components ────────────────────────────────────────────────────────────

interface ImageSlideWrapperProps {
    src: string
    alt: string
    isPriority: boolean
    styles: CarouselStyles
}

const ImageSlideWrapper: React.FC<ImageSlideWrapperProps> = ({src, alt, isPriority, styles}) => (
    <Box sx={styles.mediaCarouselSlidePortrait}>
        <ProductImage
            productImage={src}
            alt={alt}
            hasLazy={!isPriority}
            isVisible
            isPriority={isPriority}
            styles={{
                imageWrapper: styles.mediaCarouselSlideImageWrapper,
                hoverImage: styles.mediaCarouselSlideHoverImage,
            }}
        />
    </Box>
)

interface VideoSlideWrapperProps {
    src: string
    // New responsive contract (c_cloudinaryVideo). When present, forwarded to the atom as <source>
    // children; `src` stays as the legacy single-URL fallback.
    sources?: VideoSource[]
    poster?: string
    productName: string
    isHorizontal?: boolean
    styles: CarouselStyles
}

const VideoSlideWrapper: React.FC<VideoSlideWrapperProps> = ({src, sources, poster, productName, isHorizontal, styles}) => (
    <Box sx={isHorizontal ? styles.mediaCarouselSlideLandscape : styles.mediaCarouselSlidePortrait}>
        <ProductImage
            productImage=""
            videoSrc={src}
            sources={sources}
            videoPoster={poster}
            alt={productName}
            isTileVideo={false}
            styles={{
                imageWrapper: styles.mediaCarouselSlideImageWrapper,
                hoverImage: {},
            }}
        />
    </Box>
)

export default ProductMediaCarousel
